import * as Sentry from '@sentry/react';

import { AuthenticationData } from '../store/authentication';

export class ApiError extends Error {
    constructor(
        public readonly statusCode: number,
        message: string,
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

type ResponseType = 'json' | 'blob' | 'text' | 'void';

export type SentryExtra = Record<string, string | number | boolean | undefined>;

export interface ApiClientOptions {
    method?: string;
    /** Plain objects auto-serialize as JSON with `Content-Type: application/json`. FormData passes through. String bodies are sent as-is — callers must set `headers['Content-Type']` explicitly. */
    body?: unknown;
    headers?: Record<string, string>;
    responseType?: ResponseType;
    apiKey: string;
    authenticationData?: AuthenticationData;
    sentryTag: string;
    sentryExtra?: SentryExtra;
    /** Response statuses that are expected and should NOT be reported to Sentry. ApiError is still thrown so callers can branch on it. */
    silentStatusCodes?: number[];
}

const buildHeaders = (
    apiKey: string,
    authenticationData: AuthenticationData | undefined,
    body: unknown,
    extra: Record<string, string> | undefined,
): Record<string, string> => {
    const headers: Record<string, string> = { ...(extra ?? {}) };
    if (apiKey) {
        headers['apikey'] = apiKey;
    }
    if (typeof body === 'object' && body !== null && !(body instanceof FormData) && headers['Content-Type'] === undefined) {
        headers['Content-Type'] = 'application/json;charset=UTF-8';
    }
    if (authenticationData) {
        const prefix = authenticationData.scope === 'LDAP' ? 'Bearer' : authenticationData.token_type;
        headers['Authorization'] = `${prefix} ${authenticationData.access_token}`;
    }
    return headers;
};

const serializeBody = (body: unknown) => {
    if (body === undefined) return undefined;
    if (body instanceof FormData) return body;
    if (typeof body === 'string') return body;
    return JSON.stringify(body);
};

export async function apiClient<T>(url: string, options: ApiClientOptions): Promise<T> {
    const {
        method = 'GET',
        body,
        headers: extraHeaders,
        responseType = 'json',
        apiKey,
        authenticationData,
        sentryTag,
        sentryExtra = {},
        silentStatusCodes,
    } = options;

    try {
        const response = await fetch(url, {
            method,
            headers: buildHeaders(apiKey, authenticationData, body, extraHeaders),
            body: serializeBody(body),
        });

        if (!response.ok) {
            const error = new ApiError(response.status, `[${sentryTag}] ${response.status} ${response.statusText}`);
            if (!silentStatusCodes?.includes(response.status)) {
                Sentry.captureException(error, {
                    tags: { api_endpoint: sentryTag },
                    extra: { ...sentryExtra, statusCode: response.status, url },
                });
            }
            throw error;
        }

        if (responseType === 'void') return undefined as T;
        if (responseType === 'blob') return (await response.blob()) as T;
        if (responseType === 'text') return (await response.text()) as T;
        return (await response.json()) as T;
    } catch (error) {
        if (!(error instanceof ApiError)) {
            Sentry.captureException(error, {
                tags: { api_endpoint: sentryTag },
                extra: { ...sentryExtra, url },
            });
        }
        throw error;
    }
}
